import { useEffect, useState } from 'react';
import { ApiError, debridApi, type DebridProvider, type DebridState } from '../api';
import { refreshAddons } from '../addons';

const SITES: Record<DebridProvider['id'], { url: string; where: string }> = {
  torbox: { url: 'https://torbox.app/settings', where: 'TorBox → Settings → API Key' },
  realdebrid: { url: 'https://real-debrid.com/apitoken', where: 'real-debrid.com/apitoken' },
  alldebrid: { url: 'https://alldebrid.com/apikeys/', where: 'AllDebrid → API-sleutels' },
  premiumize: { url: 'https://www.premiumize.me/account', where: 'Premiumize → Account → API Key' },
};

function date(t: number): string {
  return new Date(t).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Debrid met je eigen sleutel: koppelen, status en bronnen aan/uit. */
export function DebridSettings() {
  const [st, setSt] = useState<(DebridState & { allowed?: boolean }) | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    debridApi.get().then(setSt).catch(() => setSt({ allowed: false, providers: [], addon: false }));
  }, []);

  const run = async (fn: () => Promise<DebridState>, ok?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      setSt((s) => ({ ...s, ...r, allowed: true }));
      refreshAddons();
      setMsg(r.warning ? `Gekoppeld, maar bronnen aanzetten lukte niet: ${r.warning}` : ok ?? null);
      return true;
    } catch (e) {
      setMsg(e instanceof ApiError && e.message !== e.code ? e.message : 'Dat lukte niet.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!st) return <p className="placeholder-row" aria-busy="true">Laden…</p>;
  if (!st.allowed) {
    return (
      <section className="settings-block" aria-label="Debrid">
        <h2>Debrid</h2>
        <p className="subtle hint">Debrid staat uit voor dit profiel. De beheerder kan het aanzetten bij Gebruikers en rechten.</p>
      </section>
    );
  }
  const anyOk = st.providers.some((p) => p.linked && p.ok);
  return (
    <>
      <section className="settings-block" aria-label="Debrid">
        <h2>Je eigen debrid-account</h2>
        <p className="subtle hint">
          Met een TorBox- of Real-Debrid-account speel je films en series meteen af, rechtstreeks via je eigen internetverbinding.
          Je sleutel wordt versleuteld bewaard en is daarna voor niemand meer te zien, ook niet voor de beheerder.
        </p>
        <div className="db-list">
          {st.providers.map((p) => <ProviderCard key={p.id} p={p} busy={busy} run={run} />)}
        </div>
        {msg && <p className="subtle" role="status">{msg}</p>}
      </section>
      {anyOk && (
        <section className="settings-block" aria-label="Bronnen">
          <h2>Bronnen met je sleutel</h2>
          <div className="ua2-row">
            <span className="ua2-row-text">
              <label htmlFor="db-addon">Bronnen zoeken met je debrid-account</label>
              <span className="subtle">tumedia zet hiervoor Torrentio klaar met jouw sleutel, en toont alleen bronnen die bij je debrid-dienst klaarstaan. Een eigen Torrentio wordt hierdoor vervangen.</span>
            </span>
            <input id="db-addon" className="sw" type="checkbox" role="switch" checked={st.addon} disabled={busy} onChange={(e) => void run(() => debridApi.addon(e.target.checked))} />
          </div>
        </section>
      )}
    </>
  );
}

function ProviderCard({ p, busy, run }: { p: DebridProvider; busy: boolean; run: (fn: () => Promise<DebridState>, ok?: string) => Promise<boolean> }) {
  const [key, setKey] = useState('');
  const [edit, setEdit] = useState(false);
  const expired = !!p.expires && p.expires < Date.now();
  const id = `db-key-${p.id}`;
  return (
    <div className="db-card">
      <div className="db-head">
        <strong>{p.name}</strong>
        {p.linked && (
          <span className={`db-state ${p.ok && !expired ? 'is-ok' : 'is-bad'}`}>
            {!p.ok ? p.message ?? 'Werkt niet' : expired ? 'Abonnement verlopen' : p.premium ? 'Werkt · premium' : 'Werkt · geen premium'}
          </span>
        )}
      </div>
      {p.linked && !edit ? (
        <>
          <span className="subtle">
            {p.account ?? 'Gekoppeld'}
            {p.expires ? ` · ${expired ? 'verlopen op' : 'geldig tot'} ${date(p.expires)}` : ''}
          </span>
          <div className="form-actions">
            <button className="btn btn-sm" disabled={busy} onClick={() => setEdit(true)}>Sleutel vervangen</button>
            <button className="btn btn-sm" disabled={busy} onClick={() => void run(() => debridApi.refresh(), 'Opnieuw gecontroleerd.')}>Controleren</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => debridApi.unlink(p.id), `${p.name} ontkoppeld.`)}>Ontkoppelen</button>
          </div>
        </>
      ) : (
        <form
          className="db-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (key.trim()) void run(() => debridApi.link(p.id, key.trim()), `${p.name} gekoppeld.`).then((ok) => { if (ok) { setKey(''); setEdit(false); } });
          }}
        >
          <label htmlFor={id} className="subtle">API-sleutel (te vinden bij <a href={SITES[p.id].url} target="_blank" rel="noreferrer">{SITES[p.id].where}</a>)</label>
          <div className="db-form-row">
            <input id={id} className="text-input" type="password" autoComplete="off" spellCheck={false} placeholder="Plak je sleutel" value={key} onChange={(e) => setKey(e.target.value)} />
            <button className="btn btn-primary" type="submit" disabled={busy || !key.trim()}>Koppelen en testen</button>
            {edit && <button className="btn btn-ghost" type="button" onClick={() => setEdit(false)}>Annuleren</button>}
          </div>
        </form>
      )}
    </div>
  );
}
